(function () {
  'use strict';

  function create({ ttl = 30000, maxEntries = 500 } = {}) {
    const cache = new Map();
    const requests = new Map();
    let generation = 0;

    function get(key, load) {
      const entry = cache.get(key);
      if (entry && entry.expires > Date.now()) return Promise.resolve(entry.value);
      cache.delete(key);
      if (requests.has(key)) return requests.get(key).promise;
      const version = generation;
      const controller = new AbortController();
      const promise = Promise.resolve()
        .then(() => {
          if (controller.signal.aborted) throw controller.signal.reason;
          return load(controller.signal);
        })
        .then((value) => {
          if (version === generation) {
            if (cache.size >= maxEntries) cache.delete(cache.keys().next().value);
            cache.set(key, { value, expires: Date.now() + ttl });
          }
          return value;
        })
        .finally(() => {
          if (requests.get(key)?.promise === promise) requests.delete(key);
        });
      requests.set(key, { promise, controller });
      return promise;
    }

    function clear() {
      generation += 1;
      cache.clear();
      requests.forEach(({ controller }) => controller.abort());
      requests.clear();
    }

    return Object.freeze({ get, clear });
  }

  /**
   * Coalesce one rendering turn into bounded requests, grouped by region. `load` also receives how
   * many of the requested ids were asked for with each `reason`, for the app's call metrics.
   */
  function createBatch({ load, ttl = 30000, maxBatchSize = 100 }) {
    const cache = create({ ttl });
    const queued = new Map();
    const controllers = new Set();
    let timer;

    function flush() {
      timer = undefined;
      const groups = Array.from(queued.entries());
      queued.clear();
      groups.forEach(([state, entries]) => {
        for (let offset = 0; offset < entries.length; offset += maxBatchSize) {
          const chunk = entries.slice(offset, offset + maxBatchSize);
          const reasons = {};
          chunk.forEach((entry) => {
            if (entry.reason) reasons[entry.reason] = (reasons[entry.reason] || 0) + 1;
          });
          const controller = new AbortController();
          controllers.add(controller);
          Promise.resolve()
            .then(() =>
              load(
                chunk.map((entry) => entry.id),
                state,
                controller.signal,
                reasons,
              ),
            )
            .then((prices) => {
              chunk.forEach((entry) => {
                const price = prices[entry.id];
                if (!price || price.ok !== true) entry.reject(new Error(price?.message || 'Pricing unavailable'));
                else entry.resolve(price);
              });
            })
            .catch((error) => chunk.forEach((entry) => entry.reject(error)))
            .finally(() => controllers.delete(controller));
        }
      });
    }

    function get(id, state, reason) {
      return cache.get(
        String(id) + ':' + state,
        () =>
          new Promise((resolve, reject) => {
            const entries = queued.get(state) || [];
            entries.push({ id: String(id), reason, resolve, reject });
            queued.set(state, entries);
            if (timer === undefined) timer = setTimeout(flush, 0);
          }),
      );
    }

    function clear() {
      cache.clear();
      clearTimeout(timer);
      timer = undefined;
      const error = new DOMException('Pricing region changed', 'AbortError');
      queued.forEach((entries) => entries.forEach((entry) => entry.reject(error)));
      queued.clear();
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
    }

    return Object.freeze({ get, clear });
  }

  window.AboffsPricingClient = Object.freeze({ create, createBatch });
})();
